import type { LocationAppointmentCatalog } from "@/app/booking/adapters/locationAppointmentCatalog";
import {
  buildAppointmentCurrentAssignmentReturnProjection,
  type AppointmentCurrentAssignmentReturnProjection,
} from "@/app/booking/adapters/specialistAppointmentReturnHref";
import { resolveCurrentSpecialistServicesRouteCapability } from "@/app/booking/adapters/specialistServicesRouteCapability";
import { ensureAppointmentSchedulingDescendantsRemoved } from "@/app/booking/completion/appointmentSchedulingDescendantCleanup";
import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import { readAppointmentBookingDraft } from "@/app/booking/core/appointmentDraftStore";
import { appointmentSchedulingRuntime } from "@/app/booking/scheduling/appointmentSchedulingRuntime";
import type { AppointmentTimeProfessionalMutationEvidenceV1 } from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import {
  consumeAppointmentTimeServicesResumeHandoff,
  parseAppointmentTimeServicesResumeHandoff,
  type AppointmentTimeServicesResumeSearchParamsLike,
} from "@/app/booking/scheduling/appointmentTimeServicesResumeHandoff";
import { readAppointmentTimeProfessionalWorkingDraft } from "@/app/booking/scheduling/appointmentTimeProfessionalWorkingDraftStore";

export type AppointmentTimeServicesResumeRouteTarget =
  | { kind: "location-services"; locationId: string }
  | {
      kind: "specialist-services";
      locationId: string;
      userLocationId: string;
    };

type AppointmentTimeProfessionalMutationPublication = NonNullable<
  ReturnType<
    typeof appointmentSchedulingRuntime.timeProfessionalMutation.resolve
  >
>;

export type AppointmentTimeServicesResumeRouteResolution =
  | { status: "absent" }
  | { status: "invalid" }
  | { status: "invalid-cleaned" }
  | { status: "cleanup-failed" }
  | {
      status: "restored";
      sourceDraft: AppointmentBookingDraftV1;
      workingDraft: AppointmentBookingDraftV1;
      timeProfessionalMutationEvidence: AppointmentTimeProfessionalMutationEvidenceV1;
      projection: AppointmentCurrentAssignmentReturnProjection;
    };

interface AppointmentTimeServicesResumeRouteDependencies {
  readSourceDraft: typeof readAppointmentBookingDraft;
  readWorkingDraft: typeof readAppointmentTimeProfessionalWorkingDraft;
  resolveTimeProfessionalMutation: (
    input: Parameters<
      typeof appointmentSchedulingRuntime.timeProfessionalMutation.resolve
    >[0],
  ) => AppointmentTimeProfessionalMutationPublication | null;
  consumeResume: typeof consumeAppointmentTimeServicesResumeHandoff;
  resolveSpecialistServicesRouteCapability: typeof resolveCurrentSpecialistServicesRouteCapability;
  buildProjection: typeof buildAppointmentCurrentAssignmentReturnProjection;
  cleanupDescendants: typeof ensureAppointmentSchedulingDescendantsRemoved;
}

const defaultDependencies: AppointmentTimeServicesResumeRouteDependencies = {
  readSourceDraft: readAppointmentBookingDraft,
  readWorkingDraft: readAppointmentTimeProfessionalWorkingDraft,
  resolveTimeProfessionalMutation: (input) =>
    appointmentSchedulingRuntime.timeProfessionalMutation.resolve(input),
  consumeResume: consumeAppointmentTimeServicesResumeHandoff,
  resolveSpecialistServicesRouteCapability:
    resolveCurrentSpecialistServicesRouteCapability,
  buildProjection: buildAppointmentCurrentAssignmentReturnProjection,
  cleanupDescendants: ensureAppointmentSchedulingDescendantsRemoved,
};

function cleanInvalidResume(
  draftId: string,
  sourceDraft: AppointmentBookingDraftV1 | null,
  dependencies: AppointmentTimeServicesResumeRouteDependencies,
): Extract<
  AppointmentTimeServicesResumeRouteResolution,
  { status: "invalid" | "invalid-cleaned" | "cleanup-failed" }
> {
  const validation = validateAppointmentStepOneBookingDraft(sourceDraft);

  if (
    !validation.valid ||
    validation.draft.draftId !== draftId ||
    validation.draft.entryPoint.kind !== "specialist-services"
  ) {
    return { status: "invalid" };
  }

  try {
    return dependencies.cleanupDescendants({
      appointmentDraftId: validation.draft.draftId,
      preserveStaffChoiceEvidence: true,
      preserveTimeProfessionalWorkingDraft: false,
    })
      ? { status: "invalid-cleaned" }
      : { status: "cleanup-failed" };
  } catch {
    return { status: "cleanup-failed" };
  }
}

function projectionMatchesCurrentRoute(input: {
  currentHref: string;
  projection: AppointmentCurrentAssignmentReturnProjection;
  route: AppointmentTimeServicesResumeRouteTarget;
}) {
  const { currentHref, projection, route } = input;

  if (projection.href !== currentHref || projection.kind !== route.kind) {
    return false;
  }

  if (route.kind === "location-services") {
    return currentHref.startsWith(`/locations/${route.locationId}/services`);
  }

  return (
    projection.userLocationId === route.userLocationId &&
    currentHref.startsWith(
      `/locations/${route.locationId}/specialists/${route.userLocationId}/services`,
    )
  );
}

/**
 * Reauthorizes the opaque Time -> Services return pointer against a complete,
 * current Location scheduling snapshot. Neither the URL nor the stored
 * derivative can reclassify the immutable Specialist origin. Any missing,
 * stale, contradictory, throwing, or route-mismatched evidence fails closed;
 * a validated source is cleaned before an invalid derivative is abandoned.
 */
export function resolveAppointmentTimeServicesResumeRoute(input: {
  searchParams: AppointmentTimeServicesResumeSearchParamsLike;
  currentHref: string;
  route: AppointmentTimeServicesResumeRouteTarget;
  locationAppointmentCatalog: LocationAppointmentCatalog | null;
  evaluatedAtEpochMs?: number;
  dependencies?: Partial<AppointmentTimeServicesResumeRouteDependencies>;
}): AppointmentTimeServicesResumeRouteResolution {
  const dependencies = {
    ...defaultDependencies,
    ...input.dependencies,
  };
  const parsed = parseAppointmentTimeServicesResumeHandoff(
    input.searchParams,
  );

  if (!parsed.ok) {
    return { status: "invalid" };
  }

  if (parsed.draftId === null) {
    return { status: "absent" };
  }

  let sourceDraft: AppointmentBookingDraftV1 | null = null;

  try {
    sourceDraft = dependencies.readSourceDraft(parsed.draftId);
  } catch {
    return { status: "invalid" };
  }

  const sourceValidation = validateAppointmentStepOneBookingDraft(sourceDraft);

  if (
    !sourceValidation.valid ||
    sourceValidation.draft.draftId !== parsed.draftId ||
    sourceValidation.draft.entryPoint.kind !== "specialist-services"
  ) {
    return { status: "invalid" };
  }

  const source = sourceValidation.draft;
  const catalog = input.locationAppointmentCatalog;

  if (
    !catalog ||
    catalog.workplace.locationId !== source.locationId ||
    catalog.eligibilityPublication.locationId !== source.locationId ||
    catalog.eligibilityPublication.catalogVersion !==
      catalog.workplace.catalogVersion
  ) {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  let storedWorkingDraft;

  try {
    storedWorkingDraft = dependencies.readWorkingDraft(parsed.draftId);
  } catch {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  if (!storedWorkingDraft) {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  let publication: AppointmentTimeProfessionalMutationPublication | null;

  try {
    publication = dependencies.resolveTimeProfessionalMutation({
      draft: storedWorkingDraft.workingDraft,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      locationEligibilityPublication: catalog.eligibilityPublication,
      orderedCatalogOfferingIds: catalog.offerings.map(
        ({ offeringId }) => offeringId,
      ),
      presentations: catalog.professionals.map((professional) => ({
        userLocationId: professional.userLocationId,
        name: professional.name,
        description: professional.role ?? professional.category,
      })),
    });
  } catch {
    publication = null;
  }

  if (!publication) {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  const expectedProviderIdentity = {
    providerId: appointmentSchedulingRuntime.timeProfessionalMutation.providerId,
    evidenceVersion:
      appointmentSchedulingRuntime.timeProfessionalMutation.evidenceVersion,
  };
  let consumed;

  try {
    consumed = dependencies.consumeResume({
      searchParams: input.searchParams,
      sourceDraft: source,
      currentEvidence: publication.evidence,
      currentLocationEligibilityPublication: catalog.eligibilityPublication,
      expectedProviderIdentity,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    });
  } catch {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  if (consumed.status !== "restored") {
    if (consumed.status === "cleanup-failed") {
      return { status: "cleanup-failed" };
    }

    if (consumed.status === "invalid-cleaned") {
      return { status: "invalid-cleaned" };
    }

    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  let projection: AppointmentCurrentAssignmentReturnProjection | null = null;
  let currentSpecialistServicesRouteCapability: ReturnType<
    typeof resolveCurrentSpecialistServicesRouteCapability
  > = null;

  try {
    currentSpecialistServicesRouteCapability =
      dependencies.resolveSpecialistServicesRouteCapability({
        draft: consumed.workingDraft,
        currentLocationEligibilityPublication:
          catalog.eligibilityPublication,
      });
  } catch {
    currentSpecialistServicesRouteCapability = null;
  }

  try {
    projection = dependencies.buildProjection({
      sourceDraft: consumed.sourceDraft,
      workingDraft: consumed.workingDraft,
      timeProfessionalMutationEvidence:
        consumed.timeProfessionalMutationEvidence,
      currentLocationEligibilityPublication: catalog.eligibilityPublication,
      expectedTimeProfessionalProviderIdentity: expectedProviderIdentity,
      currentSpecialistServicesRouteCapability,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    });
  } catch {
    projection = null;
  }

  if (
    !projection ||
    !projectionMatchesCurrentRoute({
      currentHref: input.currentHref,
      projection,
      route: input.route,
    })
  ) {
    return cleanInvalidResume(parsed.draftId, source, dependencies);
  }

  return {
    status: "restored",
    sourceDraft: consumed.sourceDraft,
    workingDraft: consumed.workingDraft,
    timeProfessionalMutationEvidence:
      consumed.timeProfessionalMutationEvidence,
    projection,
  };
}
